import React from 'react';
import {
  useNotify,
} from 'core/hooks';
import Select from 'core/components/Select';
import clsx from 'clsx';
import {
  useUpdateFaxStatusHandler,
} from 'pages/Dashboard/pages/Fax/hooks';
import noop from 'lodash/noop';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import CallReceivedRoundedIcon from '@mui/icons-material/CallReceivedRounded';
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import ToolTip from 'core/components/Tooltip';

const options = ['New', 'Read', 'Archived'];
export type FaxStatus = typeof options[number];
type HandleFaxStatusChange = (...event: any[]) => void;
type FaxSelectProps = {
  id: number;
  status: FaxStatus;
  category: string;
  errorMessage?: string | null;
  onChange: () => void;
  direction: string;
};

export default function FaxSelect({
  id,
  status,
  category: _category,
  errorMessage,
  onChange,
  direction,
}: FaxSelectProps) {
  const {
    mutateAsync: updateFaxStatus,
    isPending: isUpdating,
  } = useUpdateFaxStatusHandler();
  const notify = useNotify();
  const colors = useColorsHashMap();
  const category = _category === 'Send Failed' ? 'Failed' : _category;

  const handleChange: HandleFaxStatusChange = async (internalStatus: string) => {
    await updateFaxStatus({ inboundFaxId: id, internalStatus });
    notify({
      variant: 'success',
      message: 'Fax status updated successfully',
    });
    onChange();
  };

  const isOutbound = direction === 'Outbound';
  const isFailed = category === 'Failed';

  const value = isOutbound ? category : status as string;
  const callMadeClass = clsx(
    'text-green-800 !w-4 m-auto',
    { '!text-red-800': isFailed },
  );
  const Icon = isOutbound ? CallMadeRoundedIcon : CallReceivedRoundedIcon;
  const iconProps = {
    title: isOutbound ? 'Outbound' : 'Inbound',
    className: isOutbound ? callMadeClass : 'text-blue-800 !w-4 m-auto',
  };

  return (
    <div
      className="flex flex-row gap-2"
      role="button"
      tabIndex={-1}
      onClick={(event) => { event.stopPropagation(); }}
      onKeyDown={noop}
    >
      <Select<string>
        className="!pointer-events-auto w-28 text-center"
        selectClassName="!border-0"
        id={id.toString()}
        options={isOutbound ? [category] : options}
        getValue={(option) => option}
        getLabel={(option) => option}
        onChange={handleChange}
        value={value}
        disabled={isOutbound}
        renderValue={(value) => (
          <ToolTip title={errorMessage} hide={!isFailed}>
            <span>{isUpdating ? 'Updating...' : value}</span>
          </ToolTip>
        )}
        muiClasses={{
          select: clsx(
            '!min-h-none !py-1',
            colors[value ?? ''],
            { '!bg-gray-100 !cursor-wait': isUpdating },
          ),
        }}
      />
      <ToolTip title={iconProps.title}>
        <Icon className={iconProps.className} />
      </ToolTip>
    </div>
  );
}
